<script setup lang="ts">
  import { useAuthorsQuery } from '@/composables/useAuthorsQuery'
  import type { Credits } from '@/types/Credits'
  import { KsExpand, KsExpandItem } from '@aschehoug/kloss'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import CreditsAuthor from './CreditsAuthor.vue'
  import Typography from '@/components/common/Typography.vue'
  import { useI18n } from 'vue-i18n'

  const props = defineProps<{
    credits?: Credits
  }>()

  const { t } = useI18n()
  const authors = useAuthorsQuery(props.credits?.authorIds)
  const editors = useAuthorsQuery(props.credits?.editorIds)
</script>

<template>
  <div class="credits-wrapper">
    <Typography variant="h2">
      {{ t('credits') }}
    </Typography>

    <KsExpand :multiple="false">
      <KsExpandItem
        v-if="authors.data.value?.length"
        title="Forfattere"
        level="h3"
      >
        <template #content>
          <div
            v-for="author in authors.data.value"
            :key="author.firstname"
            class="author-wrapper"
          >
            <CreditsAuthor :author="author" />
          </div>
        </template>
      </KsExpandItem>
      <KsExpandItem
        v-if="editors.data.value?.length"
        title="Redaktører"
        level="h3"
      >
        <template #content>
          <div
            v-for="editor in editors.data.value"
            :key="editor.firstname"
            class="author-wrapper"
          >
            <CreditsAuthor :author="editor" />
          </div>
        </template>
      </KsExpandItem>
      <KsExpandItem
        v-if="credits?.collaborators"
        title="Samarbeidspartnere"
        level="h3"
      >
        <template #content>
          <div class="rich-text">
            <RichTextRenderer :rich-text="credits?.collaborators" />
          </div>
        </template>
      </KsExpandItem>
      <KsExpandItem
        v-if="credits?.bibliography"
        title="Litteraturliste"
        level="h3"
      >
        <template #content>
          <div class="rich-text">
            <RichTextRenderer :rich-text="credits?.bibliography" />
          </div>
        </template>
      </KsExpandItem>
    </KsExpand>
  </div>
</template>

<style lang="scss" scoped>
  $border-top-style: 1px solid var(--color-pair-background-color);

  .credits-wrapper {
    max-width: 645px;
    margin: 0 auto;
  }

  .author-wrapper {
    margin-bottom: 40px;
  }

  .rich-text {
    margin-bottom: 40px;
  }

  :deep(.ks-expand) {
    gap: 0;

    .ks-expand-content {
      transition: height .3s ease-in-out;
    }

    .ks-expand-content > div {
      padding: 0;
    }

    .ks-expand-toggle {
      @include body;

      margin-bottom: 0;

      .ks-button {
        color: var(--color-pair-background-color);
        background-color: var(--color-pair-color);
        border-top: $border-top-style;
        border-radius: initial;
        padding-left: 0;

        .ks-icon {
          right: 0;
        }
      }
    }

    .ks-expand-item:last-child::after {
      content: '';
      display: block;
      border-top: $border-top-style;
    }
  }
</style>
